import {AfterViewInit, Component, ElementRef, OnDestroy} from "@angular/core";
import {IHeaderAngularComp} from "ag-grid-angular";
import {IHeaderParams} from "ag-grid-community";
import {PlanDetailHeaderBase} from "../base/plan-detail-header";
import {DetailViewMode} from "../plan-detail-v5/detail-view-mode.enum";

// ESP-18515: upper bound on the number of animation frames to wait for AG Grid
// to insert this header component under its live `.ag-header-cell` (~0.5s at
// 60fps). Attachment normally happens within 1–2 frames; the bound simply
// prevents an unbounded loop if the cell never materialises (e.g. the column is
// destroyed before it is ever attached).
const MAX_ATTACH_FRAMES = 30;

@Component({
  standalone: false,
  selector: "plan-detail-clickable-header",
  templateUrl: "./plan-detail-clickable-header.component.html",
  styleUrls: ["./plan-detail-clickable-header.component.scss"]
})
export class PlanDetailClickableHeaderComponent extends PlanDetailHeaderBase implements IHeaderAngularComp, AfterViewInit, OnDestroy {

  private headerCell: HTMLElement | null = null;
  private attachFrame: number | null = null;

  /**
   * WCAG 2.1.1 Keyboard / 2.4.7 Focus Visible (ESP-18510): AG Grid manages
   * keyboard focus at the header CELL level (cells are `tabindex="-1"` and AG
   * Grid calls `.focus()` on them as you Tab across the header row). That left
   * the drill-down "+MAR2021" cell as an extra focus stop BEFORE its inner
   * <button>, so Tab appeared to "not move" (it landed on the same column's
   * cell first), and both the cell's focus ring and the button's own ring
   * painted at once (the dual focus ring). Whenever AG Grid focuses THIS cell,
   * hand focus straight to the inner <button> so the button is the single,
   * direct Tab stop with one visible ring; Tab then moves cleanly to the next
   * column's button.
   */
  private readonly forwardFocusToButton = (event: FocusEvent): void => {
    if (this.headerCell && event.target === this.headerCell && !this.isDisabled) {
      const button = this.headerCell.querySelector<HTMLButtonElement>("button.customHeaderLabel");
      button?.focus();
    }
  };

  /**
   * WCAG 4.1.2 Name, Role, Value / Dragon voice control (ESP-18510): AG Grid
   * renders each month header inside a `.ag-header-cell` with
   * `role="columnheader"` whose accessible name is the date label (e.g.
   * "Apr 2021"). Dragon matches that name against "click Apr 2021" and clicks
   * the CENTRE of the columnheader CELL — but the drill-down `<button>` only
   * spans the label's line height, so the cell's vertical centre often falls on
   * the surrounding cell chrome and the click activates nothing. That is why
   * only the first month (where the click happened to land on the button)
   * worked while the others did not. Forward any click that lands on the header
   * cell — but NOT on the button itself or AG Grid's own resize/menu chrome — to
   * the drill-down, so every month column is reliably operable by voice. The
   * button keeps handling its own clicks, so this never double-fires.
   */
  private readonly forwardClickToDrillDown = (event: MouseEvent): void => {
    if (this.isDisabled) return;
    const target = event.target as HTMLElement | null;
    if (!target) return;
    // The <button> already handles pointer/keyboard activation itself.
    if (target.closest("button.customHeaderLabel")) return;
    // Leave AG Grid's own header controls (column resize handle, menu/filter
    // buttons) alone so resizing/menu clicks don't trigger a drill-down.
    if (target.closest(".ag-header-cell-resize, .ag-header-cell-menu-button, .ag-header-cell-filter-button")) {
      return;
    }
    this.emitHeader(event);
  };

  constructor(private readonly elementRef: ElementRef<HTMLElement>) {
    super();
  }

  ngAfterViewInit(): void {
    // WCAG 4.1.2 / Dragon voice control (ESP-18550 reopen): AG Grid Angular
    // builds this component against a DETACHED `eGui` element and only
    // inserts it into the live `.ag-header-cell` AFTER Angular's own
    // `ngAfterViewInit` has already fired. Looking up `closest(".ag-header-cell")`
    // synchronously here therefore always returned null, so the click/focus
    // forwarding below silently never attached for ANY month column (verified
    // live: real clicks only "worked" when they happened to land directly on
    // the narrower `<button>` inside the cell, matching the reported "only the
    // first month works" symptom). Deferring to an animation frame runs this
    // after AG Grid has attached the cell to the real header row.
    //
    // ESP-18515 (reopen): AG Grid does NOT guarantee the cell is attached
    // within a SINGLE animation frame. Columns realised later in a layout pass
    // — in practice the last/rightmost month (the reported "April 2024" that
    // could not be activated by voice while the earlier months could) — are
    // inserted a few frames later, so a one-shot rAF snapshot resolved to null
    // and that column's click/focus forwarding never attached. Poll across a
    // bounded number of frames until the cell exists so EVERY month column
    // wires up reliably, regardless of AG Grid's per-column attach timing.
    this.attachToHeaderCell(MAX_ATTACH_FRAMES);
  }

  private attachToHeaderCell(remainingFrames: number): void {
    this.attachFrame = requestAnimationFrame(() => {
      const cell = this.elementRef.nativeElement.closest<HTMLElement>(".ag-header-cell");
      if (cell) {
        this.headerCell = cell;
        this.headerCell.addEventListener("focusin", this.forwardFocusToButton);
        this.headerCell.addEventListener("click", this.forwardClickToDrillDown);
        return;
      }
      if (remainingFrames > 1) {
        this.attachToHeaderCell(remainingFrames - 1);
      }
    });
  }

  ngOnDestroy(): void {
    if (this.attachFrame != null) {
      cancelAnimationFrame(this.attachFrame);
    }
    this.headerCell?.removeEventListener("focusin", this.forwardFocusToButton);
    this.headerCell?.removeEventListener("click", this.forwardClickToDrillDown);
    this.headerCell = null;
  }

  agInit(params: IHeaderParams): void {
    this.params = params;
  }

  emitHeader(event) {
    if(this.isDisabled){
      return;
    }

    let params:any = this.params;
    params.context.updateViewMode(false, params.column);

  }

  get isDisabled(){
    let params:any = this.params;

    //plan detail v2 doesn't have grid validity
    if(params.context.isGridValid && params.context.isGridValid()==false){
      return true;
    }

    return false;

  }

  /**
   * The drill-down ("+") header expands one granularity level finer
   * (Monthly -> Weekly -> Daily). It only behaves as a disclosure control while
   * it can still expand: in the Daily view the column is already at the finest
   * level, so activating it is a no-op and it is not an operable disclosure.
   */
  get isExpandable(): boolean {
    const context: any = (this.params as any)?.context;
    return context?.viewMode === DetailViewMode.Monthly || context?.viewMode === DetailViewMode.Weekly;
  }

  /**
   * WCAG 4.1.2 Name, Role, Value: expose the expand/collapse STATE so JAWS
   * announces "<header>, collapsed, button" and screen-reader users know the
   * column can be expanded (mirrors the "-" group header which already exposes
   * aria-expanded="true"). The "+" header always represents a COLLAPSED column,
   * so it returns "false" when it is an operable disclosure, and null (no
   * attribute) in the Daily view where it cannot expand any further.
   */
  get ariaExpanded(): string | null {
    return this.isExpandable ? "false" : null;
  }

  refresh(params: IHeaderParams): boolean {
    return false;
  }
}
